import {
    AsOfBadge,
    Badge,
    Banner,
    DateTime,
    DetailRow,
    FieldGrid,
    ObjectRef,
    PageHeader,
    PublicId,
    Section,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { Link, useParams } from 'react-router-dom';

import type { StaffStore } from '@/api/types';
import { ActivityCard } from '@/components/activity';
import { Comments } from '@/components/comments';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { LifecycleActions, type LifecycleAction } from '@/lib/lifecycle';
import { P } from '@/lib/permissions';
import { useOne } from '@/lib/queries';

/** The lifecycle a store's state allows (StoreReviewService::allows); a name change on an approved store takes approve / reject too (BM-92). */
export function storeActions(s: StaffStore): LifecycleAction[] {
    const base = `/staff/stores/${s.id}`;
    const nameChange =
        s.status === 'approved' && Boolean(s.pending_display_name);
    const reviewable = s.status === 'pending' || nameChange;
    return [
        {
            key: 'approve',
            path: `${base}/approve`,
            label: nameChange ? 'Approve name change' : 'Approve',
            title: nameChange ? 'Approve the new name' : 'Approve this store',
            description: nameChange
                ? `"${s.pending_display_name}" becomes the name on checkouts, receipts and the statement descriptor from now on.`
                : 'Checkout, API keys and webhooks start for this store.',
            permission: P.storesReview,
            enabled: reviewable,
        },
        {
            key: 'reject',
            path: `${base}/reject`,
            label: nameChange ? 'Reject name change' : 'Reject',
            title: nameChange ? 'Reject the new name' : 'Reject this store',
            description: nameChange
                ? 'The approved name stays; the merchant sees your reason.'
                : 'The merchant sees your reason and may submit the store again.',
            permission: P.storesReview,
            destructive: true,
            enabled: reviewable,
        },
        {
            key: 'suspend',
            path: `${base}/suspend`,
            label: 'Suspend',
            title: 'Suspend this store',
            description:
                'Checkout, API keys and webhooks stop. Products, customers and orders stay readable.',
            permission: P.storesReview,
            destructive: true,
            enabled: s.status === 'approved',
        },
        {
            key: 'reactivate',
            path: `${base}/reactivate`,
            label: 'Reactivate',
            title: 'Reactivate this store',
            description: 'Checkout, API keys and webhooks start again.',
            permission: P.storesReview,
            enabled: s.status === 'suspended',
        },
    ];
}

/** BM-93 (design 0.9.0 `backoffice/store`): the store, its merchant and organization, keys, endpoints, notes, activity. */
export function StorePage() {
    const { id = '' } = useParams();
    const query = useOne<StaffStore>(`/staff/stores/${id}`);
    const s = query.data?.data;
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Stores', href: '/stores' }]}
                {...detailTitle(query, s?.display_name, id)}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                        {s ? (
                            <StatusBadge status={s.status} domain="store" />
                        ) : null}
                        {s?.is_default ? (
                            <Badge
                                variant="neutral"
                                emphasis="outline"
                                size="sm"
                            >
                                Default
                            </Badge>
                        ) : null}
                    </>
                }
                secondaryAction={
                    s ? (
                        <LifecycleActions
                            invalidate={[
                                `/staff/stores/${id}`,
                                '/staff/stores',
                            ]}
                            actions={storeActions(s)}
                        />
                    ) : null
                }
            />
            <DetailBody query={query} title="Store">
                {(store) => (
                    <div className="space-y-4">
                        {store.status === 'pending' ? (
                            <Banner
                                tone="info"
                                title="Waiting for approval"
                                data-testid="store-pending"
                            >
                                Submitted <DateTime value={store.created_at} />.
                                Checkout, keys and webhooks start once approved.
                            </Banner>
                        ) : null}
                        {store.pending_display_name ? (
                            <Banner
                                tone="info"
                                title="Name change waiting for approval"
                                data-testid="store-name-change"
                            >
                                The merchant asked for &ldquo;
                                {store.pending_display_name}&rdquo;; the
                                approved name serves until you decide.
                            </Banner>
                        ) : null}
                        {store.status === 'rejected' ||
                        store.status === 'suspended' ? (
                            <Banner
                                tone="warning"
                                title={
                                    store.status === 'rejected'
                                        ? 'Rejected'
                                        : 'Suspended'
                                }
                                data-testid="store-review"
                            >
                                {store.review_reason ?? ''}
                                {store.reviewed_at ? (
                                    <>
                                        {' '}
                                        (<DateTime value={store.reviewed_at} />)
                                    </>
                                ) : null}
                            </Banner>
                        ) : null}
                        <Section title="Store" testId="store-head">
                            <FieldGrid>
                                <DetailRow label="Merchant" layout="stacked">
                                    <ObjectRef
                                        id={store.merchant.id}
                                        label={store.merchant.display_name}
                                        href={`/merchants/${store.merchant.id}`}
                                    />
                                </DetailRow>
                                <DetailRow
                                    label="Organization"
                                    layout="stacked"
                                >
                                    {store.organization ? (
                                        <ObjectRef
                                            id={store.organization.id}
                                            label={store.organization.name}
                                            href={`/organizations/${store.organization.id}`}
                                        />
                                    ) : (
                                        '—'
                                    )}
                                </DetailRow>
                                <DetailRow
                                    label="Internal name"
                                    layout="stacked"
                                >
                                    {store.name}
                                </DetailRow>
                                <DetailRow label="Descriptor" layout="stacked">
                                    <span className="font-mono text-xs">
                                        {store.descriptor ?? '—'}
                                    </span>
                                </DetailRow>
                                <DetailRow label="Site" layout="stacked">
                                    {store.site_url ?? '—'}
                                </DetailRow>
                                <DetailRow
                                    label="Support email"
                                    layout="stacked"
                                >
                                    {store.support_email ?? '—'}
                                </DetailRow>
                                <DetailRow label="Brand" layout="stacked">
                                    <span className="inline-flex items-center gap-2">
                                        {store.brand.primary ? (
                                            <span
                                                aria-hidden="true"
                                                className="border-border-1 inline-block size-4 rounded-sm border"
                                                style={{
                                                    background:
                                                        store.brand.primary,
                                                }}
                                            />
                                        ) : null}
                                        {store.brand.primary ?? 'tenant color'}{' '}
                                        ·{' '}
                                        {store.brand.radius ?? 'tenant radius'}
                                    </span>
                                </DetailRow>
                                <DetailRow
                                    label="Allowed return hosts"
                                    layout="stacked"
                                >
                                    {store.allowed_return_hosts.length === 0 ? (
                                        '—'
                                    ) : (
                                        <span className="flex flex-wrap gap-1">
                                            {store.allowed_return_hosts.map(
                                                (h) => (
                                                    <Badge
                                                        key={`${h.scheme}://${h.host}`}
                                                        variant="neutral"
                                                        emphasis="outline"
                                                        size="sm"
                                                    >
                                                        {h.scheme}://{h.host}
                                                    </Badge>
                                                ),
                                            )}
                                        </span>
                                    )}
                                </DetailRow>
                                <DetailRow label="Since" layout="stacked">
                                    <DateTime
                                        zone="inline"
                                        value={store.created_at}
                                        format="date"
                                    />
                                </DetailRow>
                            </FieldGrid>
                        </Section>
                        <Section title="API keys" flush testId="store-keys">
                            {store.api_keys.length === 0 ? (
                                <p className="text-fg-2 m-0 px-5 py-4 [font:var(--body-sm)]">
                                    No keys.
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Prefix</TableHead>
                                            <TableHead>Mode</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Created</TableHead>
                                            <TableHead>Last used</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {store.api_keys.map((k) => (
                                            <TableRow
                                                key={k.id}
                                                data-testid={`key-${k.id}`}
                                            >
                                                <TableCell>{k.name}</TableCell>
                                                <TableCell className="font-mono text-xs">
                                                    {k.prefix}
                                                </TableCell>
                                                <TableCell>{k.env}</TableCell>
                                                <TableCell>
                                                    {k.status}
                                                </TableCell>
                                                <TableCell>
                                                    <DateTime
                                                        value={k.created_at}
                                                        format="date"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <DateTime
                                                        value={k.last_used_at}
                                                        empty="never"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Section>
                        <Section
                            title="Webhook endpoints"
                            flush
                            testId="store-endpoints"
                        >
                            {store.endpoints.length === 0 ? (
                                <p className="text-fg-2 m-0 px-5 py-4 [font:var(--body-sm)]">
                                    No endpoints.
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>URL</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {store.endpoints.map((e) => (
                                            <TableRow key={e.id}>
                                                <TableCell className="font-mono text-xs">
                                                    {e.url}
                                                </TableCell>
                                                <TableCell>
                                                    {e.status}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Section>
                        <p className="text-fg-2 m-0 text-sm">
                            <Link
                                to={`/money/charges?store_id=${store.id}`}
                                className="underline"
                                data-testid="store-charges-link"
                            >
                                Recent charges of this store
                            </Link>
                        </p>
                    </div>
                )}
            </DetailBody>
            <div className="mt-4">
                <Comments subjectType="store" subjectId={id} />
            </div>
            <div className="mt-4">
                <ActivityCard entity="store" id={id} />
            </div>
        </div>
    );
}
